import type { CookieConsenterClassNames, TranslationObject } from "react-cookie-manager/dist/types/types";

/**
 * Cookie manager styling using project's button variants and color theme.
 * Aligns with shadcn/ui Button component design system and Tailwind v4 tokens.
 *
 * Button variants used:
 * - default: brand-primary background with hover state
 * - outline: bordered style for secondary actions
 */
export const cookieManagerClassNames: CookieConsenterClassNames = {
  // Banner: full-width bottom notification
  bannerContainer:
    "cookie-banner-container pointer-events-auto rounded-md border border-black/20 bg-surface-card-dark/95 p-5 text-white shadow-2xl backdrop-blur transition focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-white",
  bannerContent: "flex flex-col items-start gap-3 text-left",
  bannerTitle: "text-base font-medium text-white",
  bannerMessage: "text-sm leading-relaxed text-white/80 max-w-xl",

  // Button: Accept all (primary action)
  acceptButton: "rcm-btn-accept",

  // Button: Decline (secondary action)
  declineButton: "rcm-btn-decline",

  // Button: Manage/Preferences (secondary action)
  manageButton: "rcm-btn-manage",

  // Modal: Preferences overlay container
  modalContainer: "cookie-preferences fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-8",
  modalContent: "w-full max-w-2xl rounded-md bg-surface-card-light p-6 text-foreground-light shadow-2xl",
  modalTitle: "text-xl font-semibold text-foreground-light",
  modalMessage: "text-sm text-foreground-muted-light",

  // Manage preferences: Cookie categories container and items
  manageCookieContainer: "mt-6 flex flex-col gap-5",
  manageCookieCategory:
    "flex items-center justify-between gap-4 rounded-md border border-surface-border-light p-4 transition hover:border-brand-secondary/40",
  manageCookieCategoryTitle: "text-sm font-medium text-foreground-light",
  manageCookieCategorySubtitle: "mt-1 text-xs text-foreground-muted-light",
  manageCookieStatusText: "mt-2 text-xs text-foreground-muted-light",

  // Toggle switch styling
  manageCookieToggle: "cookie-toggle",
  manageCookieToggleChecked: "cookie-toggle-checked",

  // Button: Save preferences (primary action)
  manageSaveButton: "rcm-btn-save",

  // Button: Cancel (secondary action)
  manageCancelButton: "rcm-btn-cancel",

  // Hidden elements
  floatingButton: "hidden",
  floatingButtonCloseButton: "hidden",
  privacyPolicyLink: "underline text-brand-secondary hidden",
  poweredByLink: "hidden",
};

export const cookieManagerTranslations: TranslationObject = {
  title: "Cookies & privacy",
  message: "We use cookies to run essential services and understand how our product is used. Choose what you allow.",
  buttonText: "Accept all",
  declineButtonText: "Only necessary",
  manageButtonText: "Preferences",
  manageTitle: "Manage cookie preferences",
  manageMessage: "Adjust which optional cookies we may use. Necessary cookies keep core functionality working and are always on.",
  manageAnalyticsTitle: "Analytics",
  manageAnalyticsSubtitle: "Helps us measure usage and improve Assistant.",
  manageSocialTitle: "Functional",
  manageSocialSubtitle: "Remembers preferences and enables enhanced product features.",
  manageAdvertTitle: "Marketing",
  manageAdvertSubtitle: "Supports personalized outreach, ads, and embedded third-party content (e.g. videos).",
  manageEssentialTitle: "Necessary",
  manageEssentialSubtitle: "Required for security, authentication, and core services.",
  manageEssentialStatus: "Status: Always enabled",
  manageEssentialStatusButtonText: "Always on",
  manageCookiesStatus: "Status: {{status}} on {{date}}",
  manageCookiesStatusConsented: "Allowed",
  manageCookiesStatusDeclined: "Disabled",
  manageSaveButtonText: "Save preferences",
  manageCancelButtonText: "Cancel",
};
